import React, { useCallback, useEffect, useState } from "react";
import { chamarAdmin, mensagemDeErro } from "../../lib/adminApi";

// Seção "Usuários" do painel, só para admins (o servidor também confere: api/admin-usuarios.ts).
// Cadastro por convite: o admin informa nome e e-mail e recebe um link de 48 h, de uso único, para
// a pessoa criar a própria senha. Um admin pode tornar outra pessoa admin ou tirar o cargo dela
// (nunca o próprio).

interface Usuario {
  email: string;
  nome: string;
  ativo: boolean;
  admin: boolean;
  tem_senha: boolean;
  pelo_slack: boolean;
  ultimo_acesso: string | null;
  criado_em: string;
  convite_expira: string | null;
}

interface Registro {
  quando: string;
  autor: string | null;
  acao: string;
  alvo: string | null;
}

const AdminUsuarios: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [eu, setEu] = useState("");
  const [historico, setHistorico] = useState<Registro[]>([]);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [link, setLink] = useState<{ para: string; url: string } | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const atualizar = useCallback(async () => {
    const [lista, hist] = await Promise.all([
      chamarAdmin("/api/admin-usuarios", { acao: "listar" }),
      chamarAdmin("/api/admin-usuarios", { acao: "historico" }),
    ]);
    if (lista.ok) {
      setUsuarios(lista.json.usuarios);
      setEu(lista.json.eu);
    } else setErro(mensagemDeErro(lista.json?.erro));
    if (hist.ok) setHistorico(hist.json.historico);
  }, []);

  useEffect(() => {
    atualizar();
  }, [atualizar]);

  const mostrarLink = (para: string, url: string) => {
    setLink({ para, url });
    setCopiado(false);
  };

  const cadastrar = async () => {
    setErro(null);
    setOcupado(true);
    try {
      const r = await chamarAdmin("/api/admin-usuarios", { acao: "criar", nome, email });
      if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
      mostrarLink(nome, r.json.link);
      setNome("");
      setEmail("");
      await atualizar();
    } finally {
      setOcupado(false);
    }
  };

  const novoLink = async (u: Usuario) => {
    const aviso = u.tem_senha
      ? `Gerar um novo link para ${u.nome}? A senha atual dessa pessoa deixa de valer e ela sai do painel até criar uma nova.`
      : `Gerar um novo link para ${u.nome}? O link anterior deixa de valer.`;
    if (!window.confirm(aviso)) return;
    setErro(null);
    const r = await chamarAdmin("/api/admin-usuarios", { acao: "novo-link", email: u.email });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
    mostrarLink(u.nome, r.json.link);
    await atualizar();
  };

  const alternarAtivo = async (u: Usuario) => {
    if (u.ativo && !window.confirm(`Desativar o acesso de ${u.nome}? A pessoa sai do painel na hora.`)) return;
    setErro(null);
    const r = await chamarAdmin("/api/admin-usuarios", { acao: "ativo", email: u.email, ativo: !u.ativo });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
    await atualizar();
  };

  const alternarAdmin = async (u: Usuario) => {
    const aviso = u.admin
      ? `Tirar o cargo de admin de ${u.nome}? A pessoa continua no painel, sem as abas de admin.`
      : `Tornar ${u.nome} admin? A pessoa passa a ver e gerenciar Usuários, Responsáveis, Integrações e Base de conhecimento, e pode tornar outras pessoas admin.`;
    if (!window.confirm(aviso)) return;
    setErro(null);
    const r = await chamarAdmin("/api/admin-usuarios", { acao: "admin", email: u.email, admin: !u.admin });
    if (!r.ok) return setErro(mensagemDeErro(r.json?.erro));
    await atualizar();
  };

  const copiar = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiado(true);
    } catch {
      setCopiado(false);
    }
  };

  const situacao = (u: Usuario) =>
    !u.ativo
      ? "Desativado"
      : u.pelo_slack
        ? u.tem_senha ? "Ativo (Slack e senha)" : "Ativo (Slack)"
        : !u.tem_senha ? (u.convite_expira ? `Convite até ${u.convite_expira}` : "Convite vencido") : "Ativo";

  return (
    <>
      <section className="metricas-bloco">
        <h2>Cadastrar pessoa</h2>
        <p className="metricas-legenda">
          A pessoa recebe um link de uso único, válido por 48 horas, para criar a própria senha. Envie o link por onde preferir.
        </p>
        <form
          className="usuarios-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (nome.trim() && email.trim()) cadastrar();
          }}
        >
          <input placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
          <input type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" disabled={ocupado || !nome.trim() || !email.trim()}>
            Cadastrar e gerar link
          </button>
        </form>
        {erro && <p className="metricas-erro">{erro}</p>}
        {link && (
          <div className="usuarios-link">
            <p>
              Link de convite para <strong>{link.para}</strong>. Ele aparece só agora: copie e envie.
            </p>
            <div className="usuarios-link-linha">
              <input readOnly value={link.url} onFocus={(e) => e.target.select()} />
              <button type="button" onClick={copiar}>
                {copiado ? "Copiado" : "Copiar"}
              </button>
              <button type="button" className="usuarios-fechar" onClick={() => setLink(null)}>
                Fechar
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="metricas-bloco">
        <h2>Pessoas com acesso</h2>
        <table className="metricas-tabela">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Situação</th>
              <th>Último acesso</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.email} className={u.ativo ? "" : "usuarios-inativo"}>
                <td>
                  {u.nome}
                  {u.admin && <span className="metricas-selo">admin</span>}
                  {u.email === eu && <span className="metricas-selo">você</span>}
                  <small>{u.email}</small>
                </td>
                <td>{situacao(u)}</td>
                <td>{u.ultimo_acesso || "nunca"}</td>
                <td className="usuarios-acoes">
                  {u.email !== eu && u.ativo && (
                    <button type="button" onClick={() => novoLink(u)}>
                      Novo link
                    </button>
                  )}
                  {u.email !== eu && u.ativo && (
                    <button type="button" onClick={() => alternarAdmin(u)}>
                      {u.admin ? "Tirar admin" : "Tornar admin"}
                    </button>
                  )}
                  {u.email !== eu && (
                    <button type="button" onClick={() => alternarAtivo(u)}>
                      {u.ativo ? "Desativar" : "Reativar"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="metricas-bloco">
        <h2>Histórico</h2>
        <ul className="metricas-contagens">
          {historico.map((h, i) => (
            <li key={i}>
              <span>
                <strong>{h.autor || "—"}</strong> {h.acao}
                {h.alvo && h.alvo !== h.autor ? ` (${h.alvo})` : ""}
              </span>
              <small>{h.quando}</small>
            </li>
          ))}
          {!historico.length && <li className="metricas-vazio">Nada registrado ainda.</li>}
        </ul>
      </section>
    </>
  );
};

export default AdminUsuarios;
